import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { catchError, forkJoin, from, mergeMap, Observable, of, switchMap } from 'rxjs';
import { UserService } from 'auth-data-access';
import { Redirect } from 'interfaces';
import { CmsService } from 'prismic';
import { ProductService } from 'product';
import { RedirectService } from 'redirect';
import { isLoginPath } from 'utils';
import { findConditionalRedirect } from '../slices/redirects/find-conditional-redirect';

interface UrlPrefix {
  [key: string]: string | undefined;
}

@Injectable({
  providedIn: 'root',
})
/**
 * If there is no language parameter it redirects to the current language versionlanguage version
 */
export class LandingPageRedirectGuard implements CanActivate {
  private router = inject(Router);
  private cms = inject(CmsService);
  private user = inject(UserService);
  private products = inject(ProductService);
  private redirectService = inject(RedirectService);

  private calcUidAndPageType(route: ActivatedRouteSnapshot) {
    let uid: string | undefined = route.url.map(u => u.path).join('--') || this.cms.homeUID;

    // Handle different types of prismic pages
    let prismicPageType = 'new_landing_page';
    const urlPrefixToPageTypes: UrlPrefix = {
      product: 'product_page',
    };

    for (const urlPrefix in urlPrefixToPageTypes) {
      if (route.url.length > 0 && urlPrefix === route.url[0].path) {
        prismicPageType = urlPrefixToPageTypes[urlPrefix];
        uid = uid?.replace(urlPrefix, '');
        break;
      }
    }

    if (uid) {
      if (uid.length > 1 && uid.startsWith('--')) {
        // remove any `--` at the beginning
        uid = uid.substring(2);
      }
    }
    return { uid, prismicPageType };
  }

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    const { uid, prismicPageType } = this.calcUidAndPageType(route);
    if (uid?.startsWith('404')) return this.handleNotFound(route);

    return forkJoin([from(this.cms.getByUid(prismicPageType, uid)), from(this.cms.getByUid('redirect', uid))]).pipe(
      mergeMap(([landingPageResp, redirect]) => {
        if (redirect) {
          const url = redirect?.data.redirect_to as string;
          if (url) return this.redirectService.redirectTo(url, route.queryParams);
        }

        // Setting the response into the route data so that the resolver can use it
        route.data = { ...route.data, landingPageData: landingPageResp };
        Object.preventExtensions(route.data);

        // If the landing page data was not found then we handle it here
        if (!landingPageResp) {
          if (isLoginPath(route.url)) {
            return this.redirectService.redirectTo('/auth/login', route.queryParams);
          }
          return this.handleNotFound(route);
        }

        if (!(landingPageResp.data.redirects as unknown[]).length) return of(true);

        return forkJoin([from(this.products.getPCProducts()), this.user.getSubscriptions()]).pipe(
          switchMap(([rateplans, subscriptions]) => {
            const activeRedirect: Redirect = findConditionalRedirect(
              landingPageResp,
              subscriptions,
              rateplans,
              this.user.getPreviouslyLoggedInData()
            );
            if (activeRedirect) return this.redirectService.redirectTo(activeRedirect.redirectUrl, route.queryParams);
            return of(true);
          })
        );
      }),
      catchError(error => {
        console.error(error);
        return of(false);
      })
    );
  }

  handleNotFound(route: ActivatedRouteSnapshot): Observable<UrlTree | boolean> {
    const fullPath = route.url.map(u => u.path).join('/');
    // Backup for the prismic not found page not being present
    if (fullPath === 'not-found') {
      return of(
        this.router.createUrlTree(['404'], {
          // queryParams: { url: route.url.toString(), urlParams: JSON.stringify(route.queryParams) },
          queryParamsHandling: 'merge',
        })
      );
    }
    return this.redirectService.redirectTo('/not-found');
  }
}
